import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  numberAttribute,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { ELIMINATION_CONFIRMATION_DELAY_MS } from '@killer-party/contracts';
import { agentCounts } from '../../domain/agent-counts';
import { twoDigits } from '../../domain/format';
import { apiErrorMessage } from '../../services/api-error';
import { injectNow } from '../../services/clock';
import { MissionService } from '../../services/mission.service';
import { PlayerSessionService } from '../../services/player-session.service';
import { reloadEvery } from '../../services/polling';
import { PublicOperationService } from '../../services/public-operation.service';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { Countdown } from '../../shared/countdown/countdown';
import { Dossier } from '../../shared/dossier/dossier';
import { GameStatus } from '../../shared/game-status/game-status';
import { PageTitle } from '../../shared/page-title/page-title';
import { StatePanel } from '../../shared/state-panel/state-panel';
import { TargetCard } from '../../shared/target-card/target-card';

// orders : cible a eliminer ; claimed : elimination declaree, en attente de la
// cible ; threatened : elimination declaree contre le joueur (TD-015).
type MissionView = 'orders' | 'claimed' | 'threatened';

// Ordre de mission d'un agent en jeu : cible, action, elimination (TD-014, TD-015).
// Le joueur en jeu ne voit que les compteurs, jamais la fiche d'effectif (TD-021).
@Component({
  selector: 'app-mission-page',
  imports: [
    ConfirmDialog,
    Countdown,
    DatePipe,
    Dossier,
    GameStatus,
    PageTitle,
    StatePanel,
    TargetCard,
  ],
  templateUrl: './mission-page.html',
})
export class MissionPage {
  readonly missionId = input.required({ transform: numberAttribute });

  private readonly router = inject(Router);
  private readonly missions = inject(MissionService);
  private readonly session = inject(PlayerSessionService);
  private readonly now = injectNow();

  protected readonly mission = this.missions.mission(() => this.missionId());
  protected readonly target = this.missions.target(() => this.missionId());
  protected readonly threat = this.missions.pendingElimination(() => this.missionId());
  protected readonly operation = inject(PublicOperationService).operation(
    () => this.mission.value()?.operationPublicId,
  );

  protected readonly codeName = computed(() => this.session.codeNameFor(this.missionId()));
  protected readonly counts = computed(() => agentCounts(this.operation.value()?.ranking ?? []));
  protected readonly score = computed(() =>
    twoDigits(this.mission.value()?.succeededTargetCount ?? 0),
  );
  protected readonly view = computed<MissionView>(() => {
    if (this.threat.value()) {
      return 'threatened';
    }
    return this.target.value()?.claimedAt ? 'claimed' : 'orders';
  });
  protected readonly title = computed(() => TITLES[this.view()]);
  protected readonly lede = computed(() => {
    if (this.view() === 'claimed') {
      return 'Le QG attend la confirmation de la cible.';
    }
    if (this.view() === 'threatened') {
      return null;
    }
    const codeName = this.codeName();
    return codeName ? `Agent ${codeName}, voici votre cible.` : 'Voici votre cible.';
  });
  // La victime a ce delai pour confirmer ou contester ; ensuite l'elimination est validee.
  protected readonly claimDeadline = computed(() => {
    const claimedAt = this.target.value()?.claimedAt;
    return claimedAt
      ? new Date(Date.parse(claimedAt) + ELIMINATION_CONFIRMATION_DELAY_MS).toISOString()
      : null;
  });
  // Instant du prochain changement d'action permis, tant qu'il n'est pas atteint (TD-031).
  protected readonly actionChangeLockedUntil = computed(() => {
    const next = this.target.value()?.nextActionChangeAt;
    return next && Date.parse(next) > this.now() ? next : null;
  });
  protected readonly loadError = computed(() => {
    const error = this.mission.error() ?? this.target.error() ?? this.threat.error();
    return error ? apiErrorMessage(error) : null;
  });

  protected readonly busy = signal(false);
  protected readonly commandError = signal<string | null>(null);
  protected readonly confirmingAbandon = signal(false);
  protected readonly confirmingElimination = signal(false);

  constructor() {
    // Pas encore de temps reel (lot 10) : la mission interroge l'API.
    reloadEvery(this.target, 5000);
    reloadEvery(this.threat, 5000);
    reloadEvery(this.mission, 10_000);
    reloadEvery(this.operation, 15_000);

    // Mission terminee (eliminee, gagnee) ou plus de cible : la route de la
    // mission mene a l'ecran de son statut.
    effect(() => {
      const missionOver = this.mission.value() && this.mission.value()?.statusCode !== 'STARTED';
      const noTarget = this.target.hasValue() && this.target.value() === null;
      if (missionOver || noTarget) {
        untracked(() => this.leavePage());
      }
    });
  }

  protected eliminate(): Promise<void> {
    const target = this.target.value();
    return this.run(async () => {
      if (target) {
        await this.missions.eliminate(this.missionId(), target.id);
        this.confirmingElimination.set(false);
        this.target.reload();
      }
    });
  }

  // Declaration faite par erreur : retiree tant que la cible ne l'a pas validee.
  protected cancelElimination(): Promise<void> {
    const target = this.target.value();
    return this.run(async () => {
      if (target) {
        await this.missions.cancelElimination(this.missionId(), target.id);
        this.target.reload();
      }
    });
  }

  protected changeAction(): Promise<void> {
    const target = this.target.value();
    return this.run(async () => {
      if (target) {
        await this.missions.changeAction(this.missionId(), target.id);
        this.target.reload();
      }
    });
  }

  protected confirm(): Promise<void> {
    return this.run(async () => {
      await this.missions.confirmElimination(this.missionId());
      this.leavePage();
    });
  }

  protected contest(): Promise<void> {
    return this.run(async () => {
      await this.missions.contestElimination(this.missionId());
      this.threat.reload();
    });
  }

  protected abandon(): Promise<void> {
    return this.run(async () => {
      await this.missions.abandon(this.missionId());
      this.confirmingAbandon.set(false);
      this.leavePage();
    });
  }

  private leavePage(): void {
    this.router.navigate(['/missions', this.missionId()]);
  }

  private async run(command: () => Promise<void>): Promise<void> {
    this.busy.set(true);
    this.commandError.set(null);
    try {
      await command();
    } catch (error) {
      this.commandError.set(apiErrorMessage(error));
    } finally {
      this.busy.set(false);
    }
  }
}

const TITLES: Record<MissionView, string> = {
  orders: 'Ordre de mission',
  claimed: 'Rapport transmis',
  threatened: 'Couverture menacée',
};
